import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, ImagePlus, Loader2 } from "lucide-react";
import api, { API_BASE } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const types = ["Land", "Villa", "Apartment", "Commercial", "Distressed"];
const MAX_PHOTOS = 6;

export default function ListPropertyForm({ onDone }) {
  const [form, setForm] = useState({
    owner_name: "", owner_email: "", owner_phone: "",
    property_type: "Villa", location: "", price: "", area_sqft: "",
    title_status: "Clear", notes: "",
  });
  const [photos, setPhotos] = useState([]); // [{ url, filename, previewSrc }]
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      if (!/^image\/(jpeg|png|webp|heic|heif)$/.test(f.type)) {
        toast.error(`${f.name} — unsupported type`); continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name} — over 8MB`); continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ ...data, previewSrc: `${BACKEND}${data.url}` });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      }
    }
    setPhotos((p) => [...p, ...uploaded]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (fname) => setPhotos((p) => p.filter((x) => x.filename !== fname));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[+0-9 \-()]{7,20}$/.test(form.owner_phone)) return toast.error("Enter a valid phone number");
    if (!form.price || Number(form.price) <= 0) return toast.error("Enter a valid price");
    setLoading(true);
    try {
      await api.post("/listings", {
        ...form,
        price: Number(form.price),
        area_sqft: form.area_sqft ? Number(form.area_sqft) : null,
        photos: photos.map((p) => `${BACKEND}${p.url}`),
      });
      toast.success(photos.length ? `Listing submitted with ${photos.length} photo(s).` : "Listing submitted. Our team will reach out within 48 hours.");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 mt-2" data-testid="list-property-form">
      <div><div className="bb-label">Owner Name</div><input required className="bb-input" value={form.owner_name} onChange={set("owner_name")} data-testid="list-name" /></div>
      <div><div className="bb-label">Email</div><input required type="email" className="bb-input" value={form.owner_email} onChange={set("owner_email")} data-testid="list-email" /></div>
      <div><div className="bb-label">Phone</div><input required className="bb-input" value={form.owner_phone} onChange={set("owner_phone")} placeholder="+91 98xxxxxxxx" data-testid="list-phone" /></div>
      <div><div className="bb-label">Property Type</div>
        <select className="bb-input" value={form.property_type} onChange={set("property_type")} data-testid="list-type">
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div><div className="bb-label">Location</div><input required className="bb-input" value={form.location} onChange={set("location")} data-testid="list-location" /></div>
      <div><div className="bb-label">Price (₹)</div><input required type="number" className="bb-input" value={form.price} onChange={set("price")} data-testid="list-price" /></div>
      <div><div className="bb-label">Area (sqft)</div><input type="number" className="bb-input" value={form.area_sqft} onChange={set("area_sqft")} data-testid="list-area" /></div>
      <div><div className="bb-label">Title Status</div>
        <select className="bb-input" value={form.title_status} onChange={set("title_status")} data-testid="list-title">
          <option>Clear</option><option>Under Verification</option><option>Disputed</option>
        </select>
      </div>

      {/* Photo uploads */}
      <div className="md:col-span-2">
        <div className="bb-label">Property Photos <span className="normal-case tracking-normal text-[#EDE6D3]/50">· up to {MAX_PHOTOS} · JPG/PNG/WebP · max 8MB each</span></div>
        <div className="mt-3 flex flex-wrap gap-3">
          {photos.map((p) => (
            <div key={p.filename} className="relative w-24 h-24 border border-[#EDE6D3]/15 overflow-hidden group" data-testid={`photo-thumb-${p.filename}`}>
              <img src={p.previewSrc} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removePhoto(p.filename)} data-testid={`photo-remove-${p.filename}`} className="absolute top-1 right-1 bg-[#EDE6D3]/80 text-[#0A2342] w-6 h-6 flex items-center justify-center hover:bg-[#7A1C1C] transition-colors">
                <X size={12} />
              </button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-24 h-24 border border-dashed border-[#EDE6D3]/30 flex flex-col items-center justify-center gap-1 text-[#EDE6D3]/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50"
              data-testid="photo-upload-btn"
            >
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
              <span className="text-[9px] tracking-[2px] uppercase">{uploading ? "Uploading" : "Add Photos"}</span>
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple hidden onChange={handleFiles} data-testid="photo-file-input" />
      </div>

      <div className="md:col-span-2"><div className="bb-label">Notes</div>
        <textarea className="bb-input" rows={3} value={form.notes} onChange={set("notes")} data-testid="list-notes" />
      </div>
      <div className="md:col-span-2 flex justify-end pt-3">
        <button type="submit" disabled={loading || uploading} className="btn-primary disabled:opacity-50" data-testid="list-submit">
          {loading ? "Submitting…" : "Submit Listing"}
        </button>
      </div>
    </form>
  );
}
